import type { AxiosInstance } from "axios";
import Cookies from "js-cookie";
import queryString from "query-string";
import { z } from "zod";

import type { GetLessonsParams } from "api/services/content/hooks/useGetLessons";
import { validateResponse } from "api/validateResponse";

import type { GetExercisesParams } from "./hooks/useGetExercises";
import {
  GetExercisesResponseSchema,
  type PostExercisePayload,
  PostExerciseResponseSchema,
  type PostLevelPayload,
  PostLevelResponseSchema,
  type PostTranslationPayload,
  PostTranslationResponseSchema,
  type PutExerciseArgs,
  type PutExerciseLevelArgs,
  type PutExerciseTranslationArgs,
} from "./schemas/exercises";
import { type PostFilePayload, PostFileResponseSchema } from "./schemas/files";
import type {
  PostLessonPartArgs,
  PostLessonPartTranslationArgs,
  PutLessonArgs,
  PutLessonPartArgs,
  PutLessonPartTranslationArgs,
} from "./schemas/lessons";
import {
  GetLessonsResponseSchema,
  LessonSchema,
  PostLessonPartResponseSchema,
  PostLessonPartTranslationResponseSchema,
  type PostLessonPayload,
  PostLessonResponseSchema,
} from "./schemas/lessons";

const getLibraryApiLocale = (fallback?: string): string | undefined => Cookies.get("libraryAPIlocale") || fallback;

export const createContentService = (transportV2: AxiosInstance) => ({
  // ==================== Start of exercises ====================
  async getExerciseImages() {
    const response = await transportV2.get("/media/exercises/images");
    return validateResponse(response, z.object({ images: z.array(z.string()) }), false);
  },

  async getExercises(params: GetExercisesParams) {
    const response = await transportV2.get("/activity_library/exercises", {
      params: { ...params, locale: getLibraryApiLocale(params.locale) },
      paramsSerializer: param => queryString.stringify(param),
    });
    return validateResponse(response, GetExercisesResponseSchema, false);
  },

  async getExercise(exerciseID: number, locale?: string) {
    const response = await transportV2.get(`/therapists/exercises/${exerciseID}`, {
      params: { locale: getLibraryApiLocale(locale) },
    });
    return validateResponse(response, z.any(), false);
  },

  async postExercise(data: PostExercisePayload) {
    const response = await transportV2.post("/therapists/exercise", data);
    return validateResponse(response, PostExerciseResponseSchema, false);
  },

  async postExerciseLevel(exerciseId: number, data: PostLevelPayload) {
    const response = await transportV2.post(`/therapists/exercises/${exerciseId}/level`, data);
    return validateResponse(response, PostLevelResponseSchema, false);
  },

  async postExerciseLevelTranslation(exerciseId: number, exerciseLevelId: number, data: PostTranslationPayload) {
    const response = await transportV2.post(
      `/therapists/exercises/${exerciseId}/levels/${exerciseLevelId}/translation`,
      data
    );
    return validateResponse(response, PostTranslationResponseSchema, false);
  },

  async putExercise(args: PutExerciseArgs) {
    const response = await transportV2.put(`/therapists/exercises/${args.exerciseID}`, args.data);
    return validateResponse(response, PostExerciseResponseSchema, false);
  },

  async putExerciseLevel(args: PutExerciseLevelArgs) {
    const response = await transportV2.put(
      `/therapists/exercises/${args.exerciseID}/levels/${args.exerciseLevelID}`,
      args.data
    );
    return validateResponse(response, PostLevelResponseSchema, false);
  },

  async putExerciseLevelTranslation(args: PutExerciseTranslationArgs) {
    const response = await transportV2.put(
      `/therapists/exercises/${args.exerciseID}/levels/${args.exerciseLevelID}/translations/${args.exerciseLevelTranslationID}`,
      args.data
    );
    return validateResponse(response, PostTranslationResponseSchema, false);
  },

  deleteExercise(exerciseID: number) {
    return transportV2.delete(`/therapists/exercises/${exerciseID}`).then(({ data }) => data);
  },
  // ==================== End of exercises ====================

  // ==================== Start of lessons ====================
  async getLessons(params: GetLessonsParams) {
    const response = await transportV2.get(`/activity_library/lessons`, {
      params: { ...params, locale: getLibraryApiLocale(params.locale) },
    });
    return validateResponse(response, GetLessonsResponseSchema, false);
  },

  async getLesson(lessonID: number) {
    const locale = getLibraryApiLocale();
    const response = await transportV2.get(`/activity_library/lesson/${lessonID}`, {
      params: locale ? { locale } : undefined,
    });
    return validateResponse(response, LessonSchema, false);
  },

  async postLesson(data: PostLessonPayload) {
    const response = await transportV2.post("/therapists/lesson", data);
    return validateResponse(response, PostLessonResponseSchema, false);
  },

  async postLessonPart(args: PostLessonPartArgs) {
    const response = await transportV2.post(`/therapists/lessons/${args.lessonID}/part`, args.data);
    return validateResponse(response, PostLessonPartResponseSchema, false);
  },

  async postLessonPartTranslation(args: PostLessonPartTranslationArgs) {
    const response = await transportV2.post(
      `/therapists/lessons/${args.lessonID}/part/${args.lessonPartID}/translation`,
      args.data
    );
    return validateResponse(response, PostLessonPartTranslationResponseSchema, false);
  },

  async putLesson(args: PutLessonArgs) {
    const response = await transportV2.put(`/therapists/lessons/${args.lessonID}`, args.data);
    // TODO: add schema
    return validateResponse(response, z.any(), false);
  },

  async putLessonPart(args: PutLessonPartArgs) {
    const response = await transportV2.put(`/therapists/lessons/${args.lessonID}/part/${args.lessonPartID}`, args.data);
    // TODO: add schema
    return validateResponse(response, z.any(), false);
  },

  async putLessonPartTranslation(args: PutLessonPartTranslationArgs) {
    const response = await transportV2.put(
      `/therapists/lessons/${args.lessonID}/part/${args.lessonPartID}/translation/${args.lessonPartTranslationID}`,
      args.data
    );
    // TODO: add schema
    return validateResponse(response, z.any(), false);
  },

  deleteLesson(lessonID: number) {
    return transportV2.delete(`/therapists/lessons/${lessonID}`).then(({ data }) => data);
  },
  // ==================== End of lessons ====================

  // ==================== Start of misc ====================
  async postFile(data: PostFilePayload) {
    const response = await transportV2.post("/media/exercises/upload/presign", data);
    return validateResponse(response, PostFileResponseSchema, false);
  },
  // ==================== End of misc ====================
});

export type ContentService = ReturnType<typeof createContentService>;
